/**
 * TripExpenseLinkPanel — Ist-Kosten-Verknüpfung einer Dienstreise (RK-7).
 *
 * Sucht in den gebuchten Ausgaben (`expenses`, Status `'booked'`) des
 * aktuellen Scopes und lässt sie auf `trip.linkedExpenseIds` verknüpfen (E3,
 * architecture-reisekosten.md §2/§6.1). Die Verknüpfung ist im MVP rein
 * INFORMATIV — sie hat KEINEN DATEV-Effekt (keine Doppelbuchung möglich, die
 * verknüpfte Ausgabe bleibt exakt auf ihrem bestehenden Buchungsweg).
 *
 * Zeigt zusätzlich einen Hotel-Frühstück-Hinweis (Halb-Automatik, §11.3
 * Recht-Recherche: Vorschlag, der Nutzer bestätigt selbst am Reisetag in
 * {@link TripDayMealsTable} — keine stille Ableitung aus dem Belegtext).
 *
 * @module components/travel/TripExpenseLinkPanel
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconInfo, IconSearch } from '../../icons';
import { Expense } from '../../types/expenseTypes';
import { getExpensesForUser } from '../../api/expensesApi';
import { centsToEuros } from '../../utils/moneyUtils';
import logger from '../../utils/logger';

interface TripExpenseLinkPanelProps {
  /** Effektiver Tenant-Scope (owner-UID bzw. aktiver Mandant), Muster `expensesApi`. */
  scopeUid: string;
  linkedExpenseIds: string[];
  onChange: (linkedExpenseIds: string[]) => void;
  disabled?: boolean;
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(centsToEuros(cents));
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE');
}

/**
 * Such- und Verknüpfungs-Panel für gebuchte Ausgaben (Ist-Kosten) einer Reise.
 */
const TripExpenseLinkPanel: React.FC<TripExpenseLinkPanelProps> = ({
  scopeUid,
  linkedExpenseIds,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation('travel');
  const [bookedExpenses, setBookedExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getExpensesForUser(scopeUid)
      .then((expenses) => {
        if (!cancelled) {
          setBookedExpenses(expenses.filter((e) => e.status === 'booked'));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          logger.error('TripExpenseLinkPanel: load failed', err);
          setError(String(err instanceof Error ? err.message : err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scopeUid]);

  const toggleLink = (expenseId: string): void => {
    if (disabled) return;
    const next = linkedExpenseIds.includes(expenseId)
      ? linkedExpenseIds.filter((id) => id !== expenseId)
      : [...linkedExpenseIds, expenseId];
    onChange(next);
  };

  const linkedExpenses = bookedExpenses.filter((e) => linkedExpenseIds.includes(e.id));
  const linkedSumCents = linkedExpenses.reduce((sum, e) => sum + e.grossCents, 0);

  const normalizedSearch = search.trim().toLowerCase();
  const filteredExpenses = bookedExpenses.filter((e) => {
    if (normalizedSearch.length === 0) return true;
    return (
      e.supplierName.toLowerCase().includes(normalizedSearch) ||
      (e.invoiceNumber ?? '').toLowerCase().includes(normalizedSearch)
    );
  });

  return (
    <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline rounded-xl p-4 md:p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-primary">{t('expenseLink.title')}</h2>
        <span className="text-xs text-muted">
          {t('expenseLink.linkedSum')}: <span className="text-primary font-mono">{formatCents(linkedSumCents)}</span>
        </span>
      </div>

      <div className="flex items-start gap-2 bg-[var(--tone-info-bg)] border border-[var(--tone-info-border)] rounded-lg px-3 py-2">
        <IconInfo size={14} className="mt-0.5 shrink-0 text-[var(--tone-info-fg)]" />
        <p className="text-xs text-[var(--tone-info-fg)]">{t('expenseLink.hotelBreakfastHint')}</p>
      </div>

      {!disabled && (
        <div className="relative">
          <IconSearch size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-faint" />
          <input
            type="text"
            className="w-full bg-surface border border-hairline rounded-lg pl-8 pr-3 py-2 text-sm text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder={t('expenseLink.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {loading && <p className="text-xs text-faint py-2">{t('expenseLink.loading')}</p>}
      {!loading && error && <p className="text-xs text-[var(--tone-danger-fg)] py-2">{error}</p>}

      {!loading && !error && (
        <div className="max-h-64 overflow-y-auto divide-y divide-[color:var(--border-hairline)] rounded-lg border border-hairline">
          {filteredExpenses.length === 0 && (
            <p className="text-xs text-faint px-3 py-3">{t('expenseLink.noMatch')}</p>
          )}
          {filteredExpenses.map((expense) => {
            const linked = linkedExpenseIds.includes(expense.id);
            return (
              <label
                key={expense.id}
                className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-surface-raised cursor-pointer text-sm"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <input
                    type="checkbox"
                    checked={linked}
                    onChange={() => toggleLink(expense.id)}
                    disabled={disabled}
                  />
                  <span className="min-w-0">
                    <span className="block text-primary truncate">{expense.supplierName}</span>
                    <span className="block text-faint text-xs">{formatDate(expense.expenseDate)}</span>
                  </span>
                </span>
                <span className="text-secondary font-mono text-xs whitespace-nowrap">
                  {formatCents(expense.grossCents)}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TripExpenseLinkPanel;
